import { Component, Prop, State, h, Build, Element, Host } from '@stencil/core';

const COMPONENT_NAME = 'sn-hero-header-type-2';

/**
 * Hero header component for SignNow forms pages with background document preview image,
 * a "Get Form" CTA button, and an interactive "Show details / Hide details" toggle that
 * reveals a short description while hiding the IRS description block.
 * The background image adapts to viewport size (default, large).
 * Clicking "Get Form" or the heading triggers a form-editor loading flow via
 * the `sn-popup-form-loader` component. All text content is inline-editable via slots.
 *
 * @product signnow
 * @source intro
 * @version 2.0.0
 * @category SignNow
 *
 * @slot heading - {required}, <me-heading variant="1" tag="div"> - Main hero heading text (the component already wraps this slot in the page <h1>, so the slotted me-heading must render a non-heading `div` to avoid a nested/duplicate h1)
 * @slot description - <me-text tag="p"> - Section description text below the heading
 * @slot short-description - <me-text tag="p"> - Expandable short description shown in the details panel
 * @slot irs-description - <me-text tag="p"> - IRS disclaimer text shown by default
 *
 * @example
 *   <sn-hero-header-type-2
 *     form-id="2"
 *     button-text="Get Form"
 *     show-details-text="Show details"
 *     hide-details-text="Hide details"
 *     image-default="https://www.signnow.com/preview/454/879/454879723/large.png"
 *     image-large="https://www.signnow.com/preview/454/879/454879723/large.png"
 *   >
 *     <me-heading slot="heading" variant="1" tag="div">Lease Agreement Form</me-heading>
 *     <me-text slot="description" tag="p">Create a custom Lease Agreement Form that meets your industry specifications.</me-text>
 *     <me-text slot="short-description" tag="p">Residential Lease Agreement THIS LEASE AGREEMENT hereinafter referred to as the Agreement is entered into between the landlord and the tenant.</me-text>
 *     <me-text slot="irs-description" tag="p">We are not affiliated with any brand or entity on this form.</me-text>
 *   </sn-hero-header-type-2>
 */
@Component({
  tag: 'sn-hero-header-type-2',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnHeroHeaderType2 {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Unique form identifier used for the editor loading flow
   */
  @Prop({ attribute: 'form-id', reflect: true }) formId!: string;

  /**
   * CTA button label text
   * @content
   * @default Get Form
   */
  @Prop({ attribute: 'button-text' }) buttonText: string = 'Get Form';

  /**
   * Label for the "Show details" toggle button
   * @content
   * @default Show details
   */
  @Prop({ attribute: 'show-details-text' }) showDetailsText: string = 'Show details';

  /**
   * Label for the "Hide details" toggle button
   * @content
   * @default Hide details
   */
  @Prop({ attribute: 'hide-details-text' }) hideDetailsText: string = 'Hide details';

  /**
   * Background image URL for default / mobile viewports
   */
  @Prop({ attribute: 'image-default' }) imageDefault: string = '';

  /**
   * Background image URL for large viewports (960px+)
   */
  @Prop({ attribute: 'image-large' }) imageLarge: string = '';

  @State() detailsOpen = false;
  @State() computedMinHeight: string | undefined;

  @Element() el: HTMLElement;

  private resizeObserver?: ResizeObserver;

  componentDidLoad() {
    if (!Build.isBrowser) return;
    this.calculateHeight();
    this.resizeObserver = new ResizeObserver(() => this.calculateHeight());
    this.resizeObserver.observe(document.documentElement);
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  private calculateHeight() {
    const host = this.el;
    let offsetHeight = 0;
    let sibling = host.previousElementSibling;
    while (sibling) {
      offsetHeight += (sibling as HTMLElement).offsetHeight || 0;
      sibling = sibling.previousElementSibling;
    }
    this.computedMinHeight = `calc(100dvh - ${offsetHeight}px)`;
  }

  private toggleDetails = () => { this.detailsOpen = !this.detailsOpen; };

  private handleCtaClick = (event: Event) => {
    event.preventDefault();
    if (!Build.isBrowser) return;
    window.dispatchEvent(new CustomEvent('sn-form-loader-open', { detail: this.formId }));
  };

  private handleH1Click = (event: Event) => { this.handleCtaClick(event); };

  private handleH1KeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleCtaClick(event);
    }
  };

  render() {
    return (
      <Host data-color-mode={this.colorMode}>
        <section class={COMPONENT_NAME} data-formid={this.formId} style={{ minHeight: this.computedMinHeight }}>
          {this.imageDefault && (
            <picture class={`${COMPONENT_NAME}__background`}>
              {this.imageLarge && <source media="(min-width: 960px)" srcSet={this.imageLarge} />}
              <img src={this.imageDefault} alt="" loading="eager" class={`${COMPONENT_NAME}__background-img`} />
            </picture>
          )}

          <div class={`${COMPONENT_NAME}__content`}>
            {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
            <h1
              class={`${COMPONENT_NAME}__heading`}
              onClick={this.handleH1Click}
              onKeyDown={this.handleH1KeyDown}
              tabIndex={0}
            >
              <slot name="heading"></slot>
            </h1>
            {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}

            <div class={`${COMPONENT_NAME}__description`}>
              <slot name="description"></slot>
            </div>

            <me-button
              variant="primary"
              tag="button"
              class={`${COMPONENT_NAME}__cta js-editor-link`}
              clickHandler={this.handleCtaClick}
            >
              {this.buttonText}
            </me-button>
          </div>

          <div
            class={{
              [`${COMPONENT_NAME}__details`]: true,
              [`${COMPONENT_NAME}__details--open`]: this.detailsOpen,
            }}
          >
            <button
              class={`${COMPONENT_NAME}__details-toggle`}
              onClick={this.toggleDetails}
              aria-expanded={this.detailsOpen ? 'true' : 'false'}
              aria-controls={`${COMPONENT_NAME}-details-panel`}
            >
              <span>{this.detailsOpen ? this.hideDetailsText : this.showDetailsText}</span>
              <me-icon
                name="arrows--chevron-down"
                class={`${COMPONENT_NAME}__details-icon${this.detailsOpen ? ` ${COMPONENT_NAME}__details-icon--open` : ''}`}
              ></me-icon>
            </button>

            <div
              id={`${COMPONENT_NAME}-details-panel`}
              class={{
                [`${COMPONENT_NAME}__details-panel`]: true,
                [`${COMPONENT_NAME}__details-panel--open`]: this.detailsOpen,
              }}
              role="region"
            >
              <div class={{
                [`${COMPONENT_NAME}__short-description`]: true,
                [`${COMPONENT_NAME}__short-description--hidden`]: !this.detailsOpen,
              }}>
                <slot name="short-description"></slot>
              </div>
            </div>

            <div class={{
              [`${COMPONENT_NAME}__irs-description`]: true,
              [`${COMPONENT_NAME}__irs-description--hidden`]: this.detailsOpen,
            }}>
              <slot name="irs-description"></slot>
            </div>
          </div>
        </section>
      </Host>
    );
  }
}
